'use client'

import { useState, FormEvent, useEffect } from 'react'
import { X, Send, Sparkles, RefreshCw, Wand2, Plus, Trash2, MapPin, Calendar, Clock, Users, Megaphone, Monitor, Settings, Printer } from 'lucide-react'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { Language } from '@/lib/brief613/translations'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface BriefFormProps {
  userId: string
  onSuccess: () => void
  onCancel: () => void
}

const OBJECTIVES = [
  'Brand awareness',
  'Product/service launch',
  'Lead generation',
  'Engagement / social reach',
  'Rebranding / image refresh',
  'Other',
]

const REACTIONS = [
  'Discover our brand',
  'Trust our expertise',
  'Try or buy our product',
  'Share or engage online',
  'Visit a website / store',
  'Other',
]

const TONES = [
  'Emotional',
  'Informative',
  'Premium / Elegant',
  'Fun / Playful',
  'Serious / Corporate',
  'Inspirational',
  'Other',
]

const DELIVERABLES_OPTIONS = [
  'TV spot',
  'Social media visuals',
  'Short video',
  'Poster / Print',
  'Website banner',
  'Radio spot',
  'Email campaign',
  'Display ads',
  'Outdoor / Billboard',
  'Brochure / Flyer',
  'Infographic',
  'Animation / Motion graphics',
  'Podcast',
  'Webinar',
  'Event materials',
  'Packaging design',
  'Point of sale (POS)',
  'Digital banner ads',
  'Video ad (long form)',
  'Story / Reel',
  'Carousel post',
  'Blog post',
  'Press release',
  'White paper',
  'Case study',
  'Other',
]

const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Poland',
  'Portugal',
  'Greece',
  'Ireland',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Algeria',
  'Morocco',
  'Tunisia',
  'Egypt',
  'South Africa',
  'Nigeria',
  'Kenya',
  'Ghana',
  'Senegal',
  'Ivory Coast',
  'Cameroon',
  'Mali',
  'Burkina Faso',
  'Niger',
  'Chad',
  'Mauritania',
  'China',
  'Japan',
  'South Korea',
  'India',
  'Thailand',
  'Vietnam',
  'Indonesia',
  'Philippines',
  'Malaysia',
  'Singapore',
  'Brazil',
  'Argentina',
  'Chile',
  'Colombia',
  'Peru',
  'Mexico',
  'Venezuela',
  'Saudi Arabia',
  'United Arab Emirates',
  'Qatar',
  'Kuwait',
  'Turkey',
  'Russia',
  'Ukraine',
  'Israel',
  'New Zealand',
  'Other',
]

export default function BriefForm({ userId, onSuccess, onCancel }: BriefFormProps) {
  const [language, setLanguage] = useState<Language>('en')
  const t = useBrief613Translation(language)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [aiLoading, setAiLoading] = useState<{ [key: string]: boolean }>({})
  const [briefType, setBriefType] = useState<'event' | 'digital_marketing' | 'print' | 'custom' | ''>('')

  useEffect(() => {
    // Load language preference from localStorage
    const savedLang = localStorage.getItem('brief613_language') as Language
    if (savedLang && ['en', 'fr', 'ar', 'ru', 'zh'].includes(savedLang)) {
      setLanguage(savedLang)
    }
  }, [])
  const [customSections, setCustomSections] = useState<{ [key: string]: boolean }>({
    campaign_basics: true,
    target_audience: true,
    messaging: true,
    deliverables: true,
    event_info: false,
    digital_marketing: false,
    print_materials: false,
    inspiration: true,
    brand_assets: true,
    constraints: true,
    budget: true,
    contact: true,
    notes: true,
  })
  const [formData, setFormData] = useState<any>({
    objective: [],
    objective_other: '',
    target_audience: '',
    desired_reaction: '',
    reaction_other: '',
    key_message: '',
    tone: '',
    tone_other: '',
    deliverables: [],
    deliverables_other: '',
    deadline: '',
    approval_contact_name: '',
    approval_contact_email: '',
    campaign_title: '',
    inspiration_urls: [''],
    brand_assets: '',
    constraints: '',
    budget: '',
    additional_notes: '',
    number_of_participants: '',
    event_locations: [{ 
      country: '', 
      address: '', 
      date_start: '', 
      date_end: '', 
      time_start: '', 
      time_end: '', 
      participants: '' 
    }],
    event_date: '',
    event_time: '',
  })

  const handleAIGenerate = async (field: string, question: string, enhanceMode: boolean = false) => {
    setAiLoading((prev) => ({ ...prev, [field]: true }))

    try {
      const response = await fetch('/api/brief613/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          question,
          brief_context: {
            objective: Array.isArray(formData.objective) ? formData.objective.join(', ') : formData.objective,
            target_audience: formData.target_audience,
            desired_reaction: formData.desired_reaction,
            key_message: formData.key_message,
            tone: formData.tone,
          },
          enhance_mode: enhanceMode,
          original_text: enhanceMode ? formData[field] || '' : '',
        }),
      })

      const result = await response.json()

      if (result.success) {
        setFormData((prev: any) => ({
          ...prev,
          [field]: result.data.suggestion,
        }))
      } else {
        alert(result.error || 'Failed to generate suggestion')
      }
    } catch (err: any) {
      alert('Error generating AI suggestion: ' + err.message)
    } finally {
      setAiLoading((prev) => ({ ...prev, [field]: false }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Process form data
    let objective = Array.isArray(formData.objective) ? formData.objective.join(', ') : formData.objective
    if (formData.objective.includes('Other') && formData.objective_other) {
      objective = objective.replace('Other', formData.objective_other)
    }

    let desiredReaction = formData.desired_reaction
    if (desiredReaction === 'Other' && formData.reaction_other) {
      desiredReaction = formData.reaction_other
    }

    let tone = formData.tone
    if (tone === 'Other' && formData.tone_other) {
      tone = formData.tone_other
    }

    let deliverables = Array.isArray(formData.deliverables) ? formData.deliverables : [formData.deliverables]
    if (deliverables.includes('Other') && formData.deliverables_other) {
      deliverables = deliverables.filter((d: string) => d !== 'Other')
      deliverables.push(formData.deliverables_other)
    }

    // Validate brief type
    if (!briefType) {
      setError(t.brief.form.selectBriefType)
      setLoading(false)
      return
    }

    // Validate based on brief type - ONLY for event type (not for custom, digital_marketing, or print)
    if (briefType === 'event') {
      // Event-specific validation - only required for event briefs
      const hasValidLocation = formData.event_locations && formData.event_locations.length > 0 && 
        formData.event_locations.some((loc: any) => loc.country || loc.address || loc.date_start || loc.date_end || loc.time_start || loc.time_end || loc.participants)
      
      const totalParticipants = parseInt(formData.number_of_participants || '0', 10)
      
      if (totalParticipants === 0 || !hasValidLocation) {
        setError(t.brief.form.eventFieldsRequired)
        setLoading(false)
        return
      }
      
      // Validate participants per location are numbers
      for (const loc of formData.event_locations) {
        if (loc.participants && loc.participants.trim() !== '' && isNaN(parseInt(loc.participants, 10))) {
          setError(t.brief.form.participantsMustBeNumber || 'Participants must be valid numbers')
          setLoading(false)
          return
        }
      }
    }
    // For other brief types (digital_marketing, print, custom), event fields are optional - no validation needed

    // General validation
    if (
      !objective ||
      !formData.target_audience ||
      !desiredReaction ||
      !formData.key_message ||
      !tone ||
      deliverables.length === 0 ||
      !formData.deadline ||
      !formData.approval_contact_name ||
      !formData.approval_contact_email
    ) {
      setError('Please fill in all required fields')
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/brief613/briefs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          brief_type: briefType,
          objective,
          target_audience: formData.target_audience,
          desired_reaction: desiredReaction,
          key_message: formData.key_message,
          tone,
          deliverables: JSON.stringify(deliverables),
          deadline: formData.deadline,
          approval_contact_name: formData.approval_contact_name,
          approval_contact_email: formData.approval_contact_email,
          campaign_title: formData.campaign_title || null,
          inspiration_urls: formData.inspiration_urls && formData.inspiration_urls.length > 0 
            ? JSON.stringify(formData.inspiration_urls.filter((url: string) => url && url.trim().length > 0)) 
            : null,
          brand_assets: formData.brand_assets ? JSON.stringify(formData.brand_assets.split('\n').filter((url: string) => url.trim())) : null,
          constraints: formData.constraints || null,
          budget: formData.budget || null,
          additional_notes: formData.additional_notes || null,
          number_of_participants: formData.number_of_participants || null,
          event_locations: formData.event_locations && formData.event_locations.length > 0 && formData.event_locations.some((loc: any) => loc.country || loc.address)
            ? JSON.stringify(formData.event_locations.filter((loc: any) => loc.country || loc.address))
            : null,
        }),
      })

      const result = await response.json()

      if (result.success) {
        onSuccess()
      } else {
        setError(result.error || 'Failed to submit brief')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${panel} p-8 max-w-4xl mx-auto`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-white/90 sm:text-lg">{t.brief.form.title}</h2>
        <button onClick={onCancel} className="text-white/60 hover:text-white transition-colors">
          <X size={24} className="text-white/50" />
        </button>
      </div>

      {error && (
        <div className={`mb-6 ${errorBox}`}>
          {error}
        </div>
      )}

      {/* Brief Type Selector */}
      {!briefType && (
        <div className="mb-8">
          <h3 className="text-base font-semibold text-white/90 mb-4 text-center">{t.brief.form.selectType}</h3>
          <p className="text-white/70 text-center mb-6">{t.brief.form.selectTypeDesc}</p>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setBriefType('event')}
              className={`${panel} p-6 rounded-xl hover:bg-white/10 transition-all border-2 border-transparent hover:border-white/20 cursor-pointer text-center`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Calendar size={24} className="text-white/50" />
                </div>
                <h4 className="text-sm font-semibold text-white/90">{t.brief.form.event}</h4>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setBriefType('digital_marketing')}
              className={`${panel} p-6 rounded-xl hover:bg-white/10 transition-all border-2 border-transparent hover:border-white/20 cursor-pointer text-center`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Monitor size={24} className="text-white/50" />
                </div>
                <h4 className="text-sm font-semibold text-white/90">{t.brief.form.digitalMarketing}</h4>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setBriefType('print')}
              className={`${panel} p-6 rounded-xl hover:bg-white/10 transition-all border-2 border-transparent hover:border-white/20 cursor-pointer text-center`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Printer size={24} className="text-white/50" />
                </div>
                <h4 className="text-sm font-semibold text-white/90">{t.brief.form.print}</h4>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setBriefType('custom')}
              className={`${panel} p-6 rounded-xl hover:bg-white/10 transition-all border-2 border-transparent hover:border-white/20 cursor-pointer text-center`}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Settings size={24} className="text-white/50" />
                </div>
                <h4 className="text-sm font-semibold text-white/90">{t.brief.form.custom}</h4>
              </div>
            </button>
          </div>
        </div>
      )}

      {briefType && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {briefType === 'event' && <Calendar size={20} className="text-white/50" />}
            {briefType === 'digital_marketing' && <Monitor size={20} className="text-white/50" />}
            {briefType === 'print' && <Printer size={20} className="text-white/50" />}
            {briefType === 'custom' && <Settings size={20} className="text-white/50" />}
            <span className="text-white/70">
              {t.brief.form.briefType}: <span className="font-semibold text-white">
                {briefType === 'event' ? t.brief.form.event : briefType === 'digital_marketing' ? t.brief.form.digitalMarketing : briefType === 'print' ? t.brief.form.print : t.brief.form.custom}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setBriefType('')
              setCustomSections({
                campaign_basics: true,
                target_audience: true,
                messaging: true,
                deliverables: true,
                event_info: false,
                digital_marketing: false,
                print_materials: false,
                inspiration: true,
                brand_assets: true,
                constraints: true,
                budget: true,
                contact: true,
                notes: true,
              })
            }}
            className="text-white/60 hover:text-white text-sm"
          >
            Change Type
          </button>
        </div>
      )}

      {briefType && (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Custom Section Selector */}
        {briefType === 'custom' && (
          <div className={`${panel} p-6 rounded-lg border border-white/10 mb-6`}>
            <h3 className="text-sm font-semibold text-white/90 mb-4">{t.brief.form.selectSections}</h3>
            <p className="text-sm text-white/60 mb-4">{t.brief.form.selectSectionsDesc}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { key: 'campaign_basics', label: t.brief.form.sections.campaignBasics },
                { key: 'target_audience', label: t.brief.form.sections.targetAudience },
                { key: 'messaging', label: t.brief.form.sections.messaging },
                { key: 'deliverables', label: t.brief.form.sections.deliverables },
                { key: 'event_info', label: t.brief.form.sections.eventInfo },
                { key: 'digital_marketing', label: t.brief.form.sections.digitalMarketing },
                { key: 'print_materials', label: t.brief.form.sections.printMaterials },
                { key: 'inspiration', label: t.brief.form.sections.inspiration },
                { key: 'brand_assets', label: t.brief.form.sections.brandAssets },
                { key: 'constraints', label: t.brief.form.sections.constraints },
                { key: 'budget', label: t.brief.form.sections.budget },
                { key: 'contact', label: t.brief.form.sections.contact },
                { key: 'notes', label: t.brief.form.sections.notes },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customSections[key]}
                    onChange={(e) => setCustomSections((prev) => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-white/55">{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Campaign Title - Always shown */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.campaign_basics)) && (
          <>
            <div>
              <label className={labelClass}>{t.brief.form.campaignTitle}</label>
              <input
                type="text"
                value={formData.campaign_title}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, campaign_title: e.target.value }))}
                className={fieldClass}
                placeholder={t.brief.form.campaignTitlePlaceholder}
              />
            </div>

            {/* Objective */}
            <div>
              <label className={labelClass}>{t.brief.form.objectiveRequired}</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {OBJECTIVES.map((obj) => (
                  <label key={obj} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.objective.includes(obj)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev: any) => ({
                            ...prev,
                            objective: [...prev.objective, obj],
                          }))
                        } else {
                          setFormData((prev: any) => ({
                            ...prev,
                            objective: prev.objective.filter((o: string) => o !== obj),
                          }))
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-white/55">{obj}</span>
                  </label>
                ))}
              </div>
              {formData.objective.includes('Other') && (
                <input
                  type="text"
                  value={formData.objective_other}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, objective_other: e.target.value }))}
                  placeholder={t.brief.form.specifyOther + ' ' + t.brief.form.objective.toLowerCase()}
                  className={fieldClass}
                />
              )}
            </div>
          </>
        )}

        {/* Target Audience */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.target_audience)) && (
        <div>
          <label className={labelClass}>{t.brief.form.targetAudienceRequired}</label>
          <div className="flex gap-2">
            <textarea
              value={formData.target_audience}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, target_audience: e.target.value }))}
              required
              className={`${fieldClass} h-auto min-h-[5rem] py-2`}
              placeholder="Who is the primary audience for this campaign?"
            />
            <button
              type="button"
              onClick={() => handleAIGenerate('target_audience', 'Who is the primary audience for this campaign?')}
              className={ghostBtn}
              disabled={aiLoading.target_audience}
            >
              {aiLoading.target_audience ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Sparkles size={18} />
              )}
            </button>
          </div>
        </div>
        )}

        {/* Desired Reaction */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.messaging)) && (
        <div>
          <label className={labelClass}>{t.brief.form.desiredReactionRequired}</label>
          <select
            value={formData.desired_reaction}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, desired_reaction: e.target.value }))}
            required
            className={fieldClass}
          >
            <option value="">{t.brief.form.selectReaction}</option>
            {REACTIONS.map((reaction) => (
              <option key={reaction} value={reaction}>
                {reaction}
              </option>
            ))}
          </select>
          {formData.desired_reaction === 'Other' && (
            <input
              type="text"
              value={formData.reaction_other}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, reaction_other: e.target.value }))}
              placeholder="Specify other reaction"
              className={fieldClass}
            />
          )}
        </div>
        )}

        {/* Key Message */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.messaging)) && (
        <div>
          <label className={labelClass}>Key Message *</label>
          <div className="flex gap-2">
            <textarea
              value={formData.key_message}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, key_message: e.target.value }))}
              required
              className={`${fieldClass} h-auto min-h-[5rem] py-2`}
              placeholder={t.brief.form.keyMessagePlaceholder}
            />
            <button
              type="button"
              onClick={() => handleAIGenerate('key_message', "What's the one key message you want to communicate?")}
              className={ghostBtn}
              disabled={aiLoading.key_message}
            >
              {aiLoading.key_message ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Sparkles size={18} />
              )}
            </button>
          </div>
        </div>
        )}

        {/* Tone */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.messaging)) && (
        <div>
          <label className={labelClass}>{t.brief.form.toneRequired}</label>
          <select
            value={formData.tone}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, tone: e.target.value }))}
            required
            className={fieldClass}
          >
            <option value="">{t.brief.form.selectTone}</option>
            {TONES.map((tone) => (
              <option key={tone} value={tone}>
                {tone}
              </option>
            ))}
          </select>
          {formData.tone === 'Other' && (
            <input
              type="text"
              value={formData.tone_other}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, tone_other: e.target.value }))}
              placeholder="Specify other tone"
              className={fieldClass}
            />
          )}
        </div>
        )}

        {/* Deliverables */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.deliverables)) && (
        <div>
          <label className={labelClass}>{t.brief.form.deliverablesRequired}</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(briefType === 'digital_marketing' 
              ? DELIVERABLES_OPTIONS.filter(d => !['Poster / Print', 'Brochure / Flyer', 'Outdoor / Billboard', 'Point of sale (POS)', 'Packaging design', 'Event materials'].includes(d))
              : briefType === 'event'
              ? DELIVERABLES_OPTIONS.filter(d => ['Event materials', 'Poster / Print', 'Brochure / Flyer', 'Social media visuals', 'Short video', 'Website banner', 'Email campaign', 'Other'].includes(d))
              : briefType === 'print'
              ? DELIVERABLES_OPTIONS.filter(d => ['Poster / Print', 'Brochure / Flyer', 'Outdoor / Billboard', 'Point of sale (POS)', 'Packaging design', 'Event materials', 'Other'].includes(d))
              : DELIVERABLES_OPTIONS
            ).map((deliverable) => (
              <label key={deliverable} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.deliverables.includes(deliverable)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData((prev: any) => ({
                        ...prev,
                        deliverables: [...prev.deliverables, deliverable],
                      }))
                    } else {
                      setFormData((prev: any) => ({
                        ...prev,
                        deliverables: prev.deliverables.filter((d: string) => d !== deliverable),
                      }))
                    }
                  }}
                  className="rounded"
                />
                <span className="text-white/55">{deliverable}</span>
              </label>
            ))}
          </div>
          {formData.deliverables.includes('Other') && (
            <input
              type="text"
              value={formData.deliverables_other}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, deliverables_other: e.target.value }))}
              placeholder="Specify other deliverable"
              className={fieldClass}
            />
          )}
        </div>
        )}

        {/* Inspiration URLs */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.inspiration)) && (
        <div>
          <label className={labelClass}>{t.brief.form.inspirationUrls}</label>
          <div className="space-y-2">
            {formData.inspiration_urls.map((url: string, index: number) => (
              <div key={index} className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    const newUrls = [...formData.inspiration_urls]
                    newUrls[index] = e.target.value
                    setFormData((prev: any) => ({ ...prev, inspiration_urls: newUrls }))
                  }}
                  className={fieldClass}
                  placeholder="https://example.com/inspiration"
                />
                {formData.inspiration_urls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newUrls = formData.inspiration_urls.filter((_: string, i: number) => i !== index)
                      setFormData((prev: any) => ({ ...prev, inspiration_urls: newUrls }))
                    }}
                    className={ghostBtn}
                    title="Remove URL"
                  >
                    <Trash2 size={18} className="text-white/50" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setFormData((prev: any) => ({
                  ...prev,
                  inspiration_urls: [...prev.inspiration_urls, ''],
                }))
              }}
              className={ghostBtn}
            >
              <Plus size={18} className="text-white/50" />
              Add URL
            </button>
          </div>
          <p className="text-xs text-white/60 mt-2">Add URLs for inspiration, reference campaigns, or examples</p>
        </div>
        )}

        {/* Brand Assets */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.brand_assets)) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>{t.brief.form.brandAssets}</label>
            <button
              type="button"
              onClick={() => handleAIGenerate('brand_assets', 'What brand assets, guidelines, logos, color palettes, fonts, or style guides should be referenced for this campaign?', true)}
              disabled={loading || aiLoading.brand_assets}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enhance with AI"
            >
              {aiLoading.brand_assets ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enhance with AI
                </>
              )}
            </button>
          </div>
          <textarea
            value={formData.brand_assets}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, brand_assets: e.target.value }))}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="Links to brand guidelines, logos, color palettes, fonts, style guides, or other brand assets"
            disabled={loading || aiLoading.brand_assets}
          />
          <p className="text-xs text-white/60 mt-1">Enter one URL per line</p>
        </div>
        )}

        {/* Constraints & Limitations */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.constraints)) && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>{t.brief.form.constraints}</label>
            <button
              type="button"
              onClick={() => handleAIGenerate('constraints', 'What are the constraints, limitations, things to avoid, restrictions, legal requirements, or mandatory elements for this campaign?', true)}
              disabled={loading || aiLoading.constraints}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enhance with AI"
            >
              {aiLoading.constraints ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enhance with AI
                </>
              )}
            </button>
          </div>
          <textarea
            value={formData.constraints}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, constraints: e.target.value }))}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="Things to avoid, restrictions, legal requirements, mandatory elements (logos, disclaimers), or any limitations"
            disabled={loading || aiLoading.constraints}
          />
        </div>
        )}

        {/* Budget */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.budget)) && (
        <div>
          <label className={labelClass}>{t.brief.form.budget}</label>
          <input
            type="text"
            value={formData.budget}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, budget: e.target.value }))}
            className={fieldClass}
            placeholder="e.g., $10,000 - $15,000 or Budget range"
          />
        </div>
        )}

        {/* Deadline & Contact - Always shown */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={labelClass}>{t.brief.form.deadlineRequired}</label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, deadline: e.target.value }))}
              required
              className={fieldClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.brief.form.approvalContactNameRequired}</label>
            <input
              type="text"
              value={formData.approval_contact_name}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, approval_contact_name: e.target.value }))}
              required
              className={fieldClass}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>{t.brief.form.approvalContactEmailRequired}</label>
            <input
              type="email"
              value={formData.approval_contact_email}
              onChange={(e) => setFormData((prev: any) => ({ ...prev, approval_contact_email: e.target.value }))}
              required
              className={fieldClass}
            />
          </div>
        </div>

        {/* Event Information Section */}
        {(briefType === 'event' || (briefType === 'custom' && customSections.event_info)) && (
        <div className="border-t border-white/10 pt-6 mt-6">
          <h3 className="text-base font-semibold text-white/90 mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-white/50" />
            {t.brief.form.eventInfo}
          </h3>
          <p className="text-sm text-white/60 mb-4">{t.brief.form.eventInfoDesc}</p>

          {/* Total Number of Participants (Auto-calculated) */}
          <div className="mb-6">
            <label className={`${labelClass} mb-2 flex items-center gap-2`}>
              <Users size={16} className="text-white/50" />
              {t.brief.form.totalParticipants}
            </label>
            <input
              type="number"
              value={formData.number_of_participants}
              readOnly
              className={fieldClass}
              placeholder={t.brief.form.numberOfParticipantsPlaceholder}
            />
            <p className="text-xs text-white/60 mt-1">{t.brief.form.totalParticipantsDesc}</p>
          </div>

          {/* Event Locations */}
          <div className="mb-6">
            <label className={`${labelClass} mb-2 flex items-center gap-2`}>
              <MapPin size={16} className="text-white/50" />
              {t.brief.form.eventLocations}
            </label>
            <p className="text-xs text-white/60 mb-3">{t.brief.form.eventLocationsDesc}</p>
            <div className="space-y-4">
              {formData.event_locations.map((location: any, index: number) => (
                <div key={index} className={`${panelInner} p-4`}>
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-sm font-medium text-white/70">{t.brief.form.location} {index + 1}</span>
                    {formData.event_locations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newLocations = formData.event_locations.filter((_: any, i: number) => i !== index)
                          setFormData((prev: any) => {
                            const updated = { ...prev, event_locations: newLocations }
                            // Recalculate total participants
                            const total = newLocations.reduce((sum: number, loc: any) => {
                              const participants = parseInt(loc.participants || '0', 10)
                              return sum + (isNaN(participants) ? 0 : participants)
                            }, 0)
                            updated.number_of_participants = total > 0 ? total.toString() : ''
                            return updated
                          })
                        }}
                        className="text-red-400/60 hover:text-red-400 transition-colors"
                        title="Remove location"
                      >
                        <Trash2 size={16} className="text-white/50" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.country}</label>
                        <select
                          value={location.country}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].country = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                        >
                          <option value="">{t.brief.form.selectCountry}</option>
                          {COUNTRIES.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.address}</label>
                        <input
                          type="text"
                          value={location.address}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].address = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                          placeholder={t.brief.form.addressPlaceholder}
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.dateStart}</label>
                        <input
                          type="date"
                          value={location.date_start}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].date_start = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.dateEnd}</label>
                        <input
                          type="date"
                          value={location.date_end}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].date_end = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.timeStart}</label>
                        <input
                          type="time"
                          value={location.time_start}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].time_start = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/70 mb-1">{t.brief.form.timeEnd}</label>
                        <input
                          type="time"
                          value={location.time_end}
                          onChange={(e) => {
                            const newLocations = [...formData.event_locations]
                            newLocations[index].time_end = e.target.value
                            setFormData((prev: any) => ({ ...prev, event_locations: newLocations }))
                          }}
                          className={fieldClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-white/70 mb-1">{t.brief.form.participantsPerLocation}</label>
                      <input
                        type="number"
                        value={location.participants}
                        onChange={(e) => {
                          const newLocations = [...formData.event_locations]
                          newLocations[index].participants = e.target.value
                          // Calculate total participants
                          const total = newLocations.reduce((sum: number, loc: any) => {
                            const participants = parseInt(loc.participants || '0', 10)
                            return sum + (isNaN(participants) ? 0 : participants)
                          }, 0)
                          setFormData((prev: any) => ({
                            ...prev,
                            event_locations: newLocations,
                            number_of_participants: total > 0 ? total.toString() : '',
                          }))
                        }}
                        min="1"
                        className={fieldClass}
                        placeholder="e.g., 50"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setFormData((prev: any) => ({
                    ...prev,
                    event_locations: [...prev.event_locations, { 
                      country: '', 
                      address: '', 
                      date_start: '', 
                      date_end: '', 
                      time_start: '', 
                      time_end: '', 
                      participants: '' 
                    }],
                  }))
                }}
                className={ghostBtn}
              >
                <Plus size={18} className="text-white/50" />
                {t.brief.form.addLocation}
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Additional Notes */}
        {(briefType === 'event' || briefType === 'digital_marketing' || briefType === 'print' || (briefType === 'custom' && customSections.notes)) && (
        <div>
          <label className={labelClass}>{t.brief.form.additionalNotes}</label>
          <textarea
            value={formData.additional_notes}
            onChange={(e) => setFormData((prev: any) => ({ ...prev, additional_notes: e.target.value }))}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="Any additional information, context, or special instructions for the creative team"
          />
        </div>
        )}

        <div className="flex justify-end gap-4">
          <button type="button" onClick={onCancel} className={ghostBtn} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnInline} disabled={loading}>
            {loading ? 'Submitting...' : (
              <>
                <Send size={18} />
                Submit Brief
              </>
            )}
          </button>
        </div>
      </form>
      )}
    </div>
  )
}

